import { create } from "@bufbuild/protobuf";
import { timestampFromDate } from "@bufbuild/protobuf/wkt";
import {
    keepPreviousData,
    queryOptions,
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
    type QueryClient,
} from "@tanstack/react-query";
import { useMemo, useRef } from "react";

import type { GitpodAPI } from "@/api";
import { useEventInterest } from "@/hooks/use-event-interest";
import { useFlagOnaAIEnabled } from "@/hooks/use-flag";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { getAgentExecutionLoader } from "@/queries/agent-execution-loader";
import { autoPaginate, DEFAULT_PAGE_SIZE } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { userPreferencesQueryKey, toPlainUserPreference } from "@/queries/user-preferences-queries";
import { useAuthenticatedUser, keys as userQueryKeys, type PlainUser } from "@/queries/user-queries";
import { agentExecutionEnvironmentIds, agentExecutionState, AgentExecutionState } from "@/utils/agent-executions";
import { playAgentDoneNotification } from "@/utils/notifications";
import { evictFromCache } from "@/utils/stream-cache";
import { isSubagentExecution, getParentExecutionId } from "@/utils/subagent";
import {
    type AgentExecution,
    AgentExecution_Phase,
    type Agent,
    type AgentCodeContext,
    type SendToAgentExecutionRequest,
    type Prompt,
    type ListPromptsRequest_Filter,
    AgentExecutionSchema,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { ResourceOperation, ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { PaginationRequestSchema } from "gitpod-next-api/gitpod/v1/pagination_pb";
import { type WorkflowExecutionAction } from "gitpod-next-api/gitpod/v1/workflow_pb";

export const Agents = {
    Ona: {
        id: "00000000-0000-0000-0000-000000006200",
    },
    DevelopmentEnvironmentSetup: {
        id: "0196d883-88dd-7447-8d18-3175979fe23b",
    },
    InEnvironmentOna: {
        id: "00000000-0000-0000-0000-000000007100",
    },
    InEnvironmentClaudeCode: {
        id: "00000000-0000-0000-0000-000000007300",
    },
    InEnvironmentCodex: {
        id: "00000000-0000-0000-0000-000000007400",
    },
    InEnvironmentGemini: {
        id: "00000000-0000-0000-0000-000000007500",
    },
    InEnvironmentClaudeAgent: {
        id: "00000000-0000-0000-0000-000000007600",
    },
    InEnvironmentCodexAgent: {
        id: "00000000-0000-0000-0000-000000007700",
    },
    InEnvironmentCodexAppAgent: {
        id: "00000000-0000-0000-0000-000000007800",
    },
};

/**
 * Only Ona agents (runner-side and in-environment) support the agent mode
 * dropdown (Agent / Plan / Ralph). Other agents (Claude Code, Codex, Gemini, …)
 * run a single fixed mode.
 */
const MODE_SWITCHING_AGENT_IDS: ReadonlySet<string> = new Set([Agents.Ona.id, Agents.InEnvironmentOna.id]);

export function agentSupportsModeSwitching(agentId: string | undefined): boolean {
    return agentId !== undefined && MODE_SWITCHING_AGENT_IDS.has(agentId);
}

export type PlainAgent = Agent;
export type PlainAgentExecution = AgentExecution;
export type PlainPrompt = Prompt;

// Annotation key recording whether the runner had RSA capability when the environment was created.
export const ANNOTATION_RSA_CAPABLE = "ona.com/rsa-capable";

/**
 * Resolves the agent ID and runner ID based on runner selection and RSA capability.
 *
 * Priority:
 * 1. Explicit dev runner selection → runner-side agent
 * 2. RSA feature flag enabled + rsaCapable → runner-side agent
 * 3. Otherwise → in-environment agent
 *
 * The caller is responsible for determining rsaCapable — either from live
 * runner capabilities or from a stored annotation.
 */
export function resolveAgentId(opts: {
    selectedAgentId?: string | null;
    devRunnerId?: string;
    rsaEnabled?: boolean;
    rsaCapable?: boolean;
    runnerId?: string;
}): { agentId: string; runnerId?: string } {
    if (opts.selectedAgentId) {
        const runnerId =
            opts.devRunnerId ?? (opts.rsaEnabled && opts.rsaCapable && opts.runnerId ? opts.runnerId : undefined);
        return { agentId: opts.selectedAgentId, runnerId };
    }
    if (opts.devRunnerId) {
        return { agentId: Agents.Ona.id, runnerId: opts.devRunnerId };
    }
    if (opts.rsaEnabled && opts.rsaCapable && opts.runnerId) {
        return { agentId: Agents.Ona.id, runnerId: opts.runnerId };
    }
    return { agentId: Agents.InEnvironmentOna.id };
}

const toPlainAgent = (agent: Agent): PlainAgent => {
    return agent;
};

export const toPlainAgentExecution = (agentExecution: AgentExecution): PlainAgentExecution => {
    return agentExecution;
};

const toPlainPrompt = (prompt: Prompt): PlainPrompt => {
    return prompt;
};

export const agentServiceQueryKey = {
    listAgents: () => keyWithPrincipal(["agents", "list"]),
    listAgentExecutions: () => keyWithPrincipal(["agent-execution", "list"]),
    byEnvironment: (environmentId?: string) => keyWithPrincipal(["agent-execution", "by-environment", environmentId]),
    getAgentExecution: (agentExecutionId?: string) => keyWithPrincipal(["agent-execution", "get", agentExecutionId]),
    createAgentExecutionConversationToken: (agentExecutionId?: string) =>
        keyWithPrincipal(["agent-execution", "create-conversation-token", agentExecutionId]),
    listPrompts: (filter?: ListPromptsRequest_Filter) => keyWithPrincipal(["prompts", "list", filter]),
    listPromptsInfinite: (search?: string) => keyWithPrincipal(["prompts", "list-infinite", search]),
    getPrompt: (promptId?: string) => keyWithPrincipal(["prompts", "get", promptId]),
};

export const handleAgentExecutionEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
        // Guard: only process events for executions already in the individual cache.
        // The CREATE handler below seeds this cache, so this skips events for
        // executions we haven't seen a CREATE event for yet.
        const previousExecution = client.getQueryData<PlainAgentExecution>(
            agentServiceQueryKey.getAgentExecution(evt.resourceId),
        );
        if (!previousExecution) {
            return;
        }
        const previousState = agentExecutionState(previousExecution);

        const agentExecution = await api.agentService.getAgentExecution({ agentExecutionId: evt.resourceId });
        const newExecution = agentExecution.agentExecution
            ? toPlainAgentExecution(agentExecution.agentExecution)
            : undefined;

        setAgentExecutionInCache(client, evt.resourceId, newExecution);

        // If this agent execution is part of a workflow and has outputs, invalidate the workflow execution outputs query
        const workflowActionId = newExecution?.metadata?.workflowActionId;
        if (workflowActionId && evt.operation === ResourceOperation.UPDATE_STATUS) {
            const { workflowQueryKey } = await import("@/queries/automation-queries");
            // Get the workflow action from cache to find the workflow execution ID
            const workflowAction = client.getQueryData<WorkflowExecutionAction>(
                workflowQueryKey.executionAction(workflowActionId),
            );
            if (workflowAction?.metadata?.workflowExecutionId) {
                await client.invalidateQueries({
                    queryKey: workflowQueryKey.executionOutputs(workflowAction.metadata.workflowExecutionId),
                });
            }
        }

        // Check if the agent execution just completed (not stopped by user).
        // Only notify for executions visible in the user's sidebar:
        // skip workflow executions, subagent executions, and executions
        // created by other users.
        if (
            newExecution &&
            previousState &&
            previousState !== AgentExecutionState.READY_FOR_REVIEW &&
            previousState !== AgentExecutionState.FAILED &&
            !newExecution.metadata?.workflowActionId &&
            !isSubagentExecution(newExecution)
        ) {
            const newState = agentExecutionState(newExecution);
            const isCompleted =
                newState === AgentExecutionState.READY_FOR_REVIEW || newState === AgentExecutionState.FAILED;
            const wasNotStoppedByUser = newExecution.spec?.desiredPhase !== AgentExecution_Phase.STOPPED;

            // Only notify if the execution was created by the current user.
            // The org-wide event stream delivers events for all executions,
            // but the sidebar only shows the current user's executions.
            const currentUser = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            const isOwnExecution = !!(
                currentUser?.id &&
                newExecution.metadata?.creator?.id &&
                currentUser.id === newExecution.metadata.creator.id
            );

            if (isCompleted && wasNotStoppedByUser && isOwnExecution) {
                try {
                    const notificationPref = await client.fetchQuery({
                        queryKey: userPreferencesQueryKey.getByKey("agent-done-notification"),
                        queryFn: async () => {
                            const { preference } = await api.userService.getPreference({
                                preference: { case: "preferenceKey", value: "agent-done-notification" },
                            });
                            return preference ? toPlainUserPreference(preference) : null;
                        },
                        staleTime: 1_000 * 10,
                    });

                    if (notificationPref?.value === "sound") {
                        playAgentDoneNotification(newExecution.id);
                    }
                } catch {
                    // Preference not found or fetch failed — skip notification
                }
            }
        }
    } else if (evt.operation === ResourceOperation.CREATE) {
        const agentExecution = await api.agentService.getAgentExecution({ agentExecutionId: evt.resourceId });
        const newExecution = agentExecution.agentExecution
            ? toPlainAgentExecution(agentExecution.agentExecution)
            : undefined;
        setAgentExecutionInCache(client, evt.resourceId, newExecution);
    } else if (evt.operation === ResourceOperation.DELETE) {
        setAgentExecutionInCache(client, evt.resourceId, undefined);
    }
};

export const useListAgentsOptions = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: agentServiceQueryKey.listAgents(),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const agents = await autoPaginate(
                (params) => api.agentService.listAgents(params),
                {},
                (response) => response.agents,
                100,
            );
            return {
                agents: agents.map(toPlainAgent),
            };
        },
        enabled: !!user,
    });
};

export const useListAgents = () => {
    return useQuery(useListAgentsOptions());
};

const useAgentExecutionLoader = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    if (!user) {
        return undefined;
    }
    return getAgentExecutionLoader(api, user.id);
};

const AGENT_EXECUTION_STALE_TIME = 5 * 60 * 1000; // 5 minutes — matches useListEnvironments

/**
 * Fetches all agent executions for a single environment, batched via a shared dataloader.
 * Multiple hooks mounting in the same render cycle share a single batched API call
 * using the environment_ids filter.
 *
 * Each environment gets its own query observer, so invalidation triggers a refetch
 * for that specific environment.
 */
export const useAgentExecutionsForEnvironment = (environmentId?: string) => {
    const loader = useAgentExecutionLoader();
    const queryClient = useQueryClient();
    const { value: isOnaAIEnabled, loading: isOnaAILoading } = useFlagOnaAIEnabled();
    useEventInterest({ resourceType: ResourceType.AGENT_EXECUTION });

    return useQuery({
        queryKey: agentServiceQueryKey.byEnvironment(environmentId),
        queryFn: async () => {
            const executions = await loader!.load(environmentId!);
            if (executions) {
                seedIndividualExecutionCache(queryClient, executions);
            }
            return executions;
        },
        enabled: !!environmentId && !!loader && !isOnaAILoading && isOnaAIEnabled,
        staleTime: AGENT_EXECUTION_STALE_TIME,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
    });
};

export type ChildEnvironmentInfo = {
    environmentId: string;
    agentExecution: PlainAgentExecution;
};

/**
 * Fetches agent executions for a set of environment IDs via the dataloader
 * and groups them for sidebar rendering.
 *
 * Each environment gets its own React Query observer. The dataloader batches
 * all concurrent .load() calls into a single ListAgentExecutions API call
 * (chunked at 25 environment IDs, matching the API limit).
 */
export const useGroupedAgentExecutions = (environmentIds: string[]) => {
    const loader = useAgentExecutionLoader();
    const queryClient = useQueryClient();
    const { value: isOnaAIEnabled, loading: isOnaAILoading } = useFlagOnaAIEnabled();

    const dedupedIds = useMemo(() => [...new Set(environmentIds.filter(Boolean))], [environmentIds]);

    const queryResults = useQueries({
        queries: dedupedIds.map((envId) => ({
            queryKey: agentServiceQueryKey.byEnvironment(envId),
            queryFn: async () => {
                const executions = await loader!.load(envId);
                if (executions) {
                    seedIndividualExecutionCache(queryClient, executions);
                }
                return executions;
            },
            enabled: !!loader && !isOnaAILoading && isOnaAIEnabled,
            staleTime: AGENT_EXECUTION_STALE_TIME,
            refetchOnReconnect: "always" as const,
            refetchOnWindowFocus: "always" as const,
        })),
    });

    // Report pending only when we have no data at all yet (initial load).
    // Once any query has resolved, render immediately — new environments'
    // executions load in the background without triggering a full skeleton.
    // When AI is confirmed disabled, queries won't fire so skip pending.
    const hasAnyData = queryResults.some((r) => r.data !== undefined);
    const isPending =
        dedupedIds.length > 0 &&
        !hasAnyData &&
        (isOnaAILoading || (isOnaAIEnabled && queryResults.some((r) => r.isPending)));
    const isError = !isPending && queryResults.some((r) => r.isError);

    // useQueries returns a new array reference on every render, so we
    // stabilize the data via a ref: only update when the per-element
    // references actually change (React Query preserves referential
    // identity when data hasn't changed).
    const queryDataRef = useRef<(PlainAgentExecution[] | undefined)[]>([]);
    const nextQueryData = queryResults.map((r) => r.data);
    if (
        nextQueryData.length !== queryDataRef.current.length ||
        nextQueryData.some((d, i) => d !== queryDataRef.current[i])
    ) {
        queryDataRef.current = nextQueryData;
    }
    const queryData = queryDataRef.current;

    const groupedExecutions = useMemo(() => {
        const environmentScoped = new Map<string, PlainAgentExecution[]>();
        const childEnvironments = new Map<string, ChildEnvironmentInfo[]>();
        const childEnvironmentIds = new Set<string>();

        // Exclude terminal agent executions (Claude Agent, Codex Agent) — they
        // are internal and should not appear as the sidebar status for an environment.
        const terminalAgentIds = new Set([Agents.InEnvironmentClaudeAgent.id, Agents.InEnvironmentCodexAgent.id]);

        // Collect all executions across all environments
        const allExecutions: PlainAgentExecution[] = [];
        for (let i = 0; i < dedupedIds.length; i++) {
            const envId = dedupedIds[i];
            const executions = queryData[i]?.filter((e) => !e.spec?.agentId || !terminalAgentIds.has(e.spec.agentId));
            if (executions && executions.length > 0) {
                environmentScoped.set(envId, [...executions]);
                allExecutions.push(...executions);
            }
        }

        // Index all executions by ID for parent lookups
        const executionsById = new Map<string, PlainAgentExecution>();
        for (const execution of allExecutions) {
            executionsById.set(execution.id, execution);
        }

        // Build parent environment → child environments mapping.
        // A child environment is one where:
        // 1. The execution has the ona.com/parent-execution-id annotation
        // 2. The child's environment differs from the parent's (create_new_environment=true)
        for (const execution of allExecutions) {
            const parentExecutionId = getParentExecutionId(execution);
            if (!parentExecutionId) {
                continue;
            }

            const parentExecution = executionsById.get(parentExecutionId);
            if (!parentExecution) {
                continue;
            }

            const parentEnvIds = new Set(agentExecutionEnvironmentIds(parentExecution));
            const childEnvIds = agentExecutionEnvironmentIds(execution);

            for (const childEnvId of childEnvIds) {
                if (parentEnvIds.has(childEnvId)) {
                    continue;
                }

                const parentEnvId = agentExecutionEnvironmentIds(parentExecution)[0];
                if (!parentEnvId) {
                    continue;
                }

                const children = childEnvironments.get(parentEnvId) || [];
                children.push({ environmentId: childEnvId, agentExecution: execution });
                childEnvironments.set(parentEnvId, children);
                childEnvironmentIds.add(childEnvId);
            }
        }

        // Filter out subagent executions when there's more than one execution for an environment.
        // Remove the Map entry entirely when no executions remain so downstream
        // consumers can rely on "key exists ⟹ non-empty array".
        for (const [environmentId, executions] of environmentScoped) {
            if (executions.length > 1) {
                const filtered = executions.filter((exec) => !isSubagentExecution(exec));
                if (filtered.length === 0) {
                    environmentScoped.delete(environmentId);
                } else {
                    environmentScoped.set(environmentId, filtered);
                }
            }
        }

        return {
            environmentScoped,
            childEnvironments,
            childEnvironmentIds,
        };
    }, [dedupedIds, queryData]);

    return {
        data: groupedExecutions,
        isPending,
        isError,
    };
};

export const useGetAgentExecutionOptions = (agentExecutionId?: string) => {
    const api = useGitpodAPI();

    return queryOptions({
        queryKey: agentServiceQueryKey.getAgentExecution(agentExecutionId),
        queryFn: async () => {
            const response = await api.agentService.getAgentExecution({
                agentExecutionId,
            });
            if (!response.agentExecution) {
                throw new Error("Agent execution not found");
            }
            return toPlainAgentExecution(response.agentExecution);
        },
        enabled: !!agentExecutionId,
    });
};

export const useGetAgentExecution = (agentExecutionId?: string) => {
    return useQuery(useGetAgentExecutionOptions(agentExecutionId));
};

export const useCreateAgentExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            agentId,
            codeContext,
            name,
            runnerId,
            sessionId,
        }: {
            agentId: string;
            codeContext?: AgentCodeContext;
            name?: string;
            runnerId?: string;
            sessionId?: string;
        }) => {
            const response = await api.agentService.startAgent({
                agentId: agentId,
                codeContext: codeContext,
                name: name,
                runnerId: runnerId,
                sessionId: sessionId ?? "",
            });
            return {
                agentExecutionId: response.agentExecutionId,
            };
        },
        onSuccess: (data, variables) => {
            // Use provided name or fallback to default
            const executionName = variables.name || "New Session";

            // Optimistically adding it to the cache so its shows up in the sidebar instantly and the page appears to load faster
            setAgentExecutionInCache(
                queryClient,
                data.agentExecutionId,
                create(AgentExecutionSchema, {
                    id: data.agentExecutionId,
                    spec: {
                        agentId: variables.agentId,
                        codeContext: variables.codeContext,
                    },
                    metadata: {
                        name: executionName,
                        createdAt: timestampFromDate(new Date()),
                    },
                    status: {
                        phase: AgentExecution_Phase.PENDING,
                    },
                }),
            );
        },
        meta: {
            method: "AgentService.StartAgent",
        },
    });
};

export const useImprovePromptForAgent = () => {
    const api = useGitpodAPI();
    return useMutation({
        mutationFn: async ({ agentId, prompt }: { agentId: string; prompt: string }) => {
            return api.agentService.improvePromptForAgent({ agentId, prompt });
        },
        meta: {
            method: "AgentService.ImprovePromptForAgent",
        },
    });
};

export const useDeleteAgentExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ agentExecutionId }: { agentExecutionId: string }) => {
            const response = await api.agentService.deleteAgentExecution({
                agentExecutionId,
            });
            return response;
        },
        onSuccess: (_data, variables) => {
            setAgentExecutionInCache(queryClient, variables.agentExecutionId, undefined);
        },
    });
};

export const useStopAgentExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ agentExecutionId }: { agentExecutionId: string }) => {
            const response = await api.agentService.stopAgentExecution({
                agentExecutionId,
            });
            return response;
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({
                queryKey: agentServiceQueryKey.getAgentExecution(variables.agentExecutionId),
            });
        },
    });
};

export const useSendToAgentExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: SendToAgentExecutionRequest) => {
            const response = await api.agentService.sendToAgentExecution(request);
            return response;
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({
                queryKey: agentServiceQueryKey.getAgentExecution(variables.agentExecutionId),
            });
        },
        meta: {
            method: "AgentService.SendToAgentExecution",
        },
    });
};

export const useCreateAgentExecutionConversationTokenOptions = (agentExecutionId?: string) => {
    const api = useGitpodAPI();
    const tokenValidDuration = 1000 * 60 * 45; // 45 minutes

    return queryOptions({
        queryKey: agentServiceQueryKey.createAgentExecutionConversationToken(agentExecutionId),
        queryFn: async (): Promise<string> => {
            if (!agentExecutionId) {
                throw new Error("No session ID provided");
            }

            const { token } = await api.agentService.createAgentExecutionConversationToken({ agentExecutionId });
            if (!token) {
                throw new Error("Failed to create token");
            }

            return token;
        },
        staleTime: tokenValidDuration,
        gcTime: tokenValidDuration,
        enabled: !!agentExecutionId,
    });
};

export const useCreateAgentExecutionConversationToken = (agentExecutionId?: string) => {
    return useQuery(useCreateAgentExecutionConversationTokenOptions(agentExecutionId));
};

/**
 * Populate the individual getAgentExecution(id) cache for each execution
 * returned by the dataloader. This ensures the event handler guard
 * (handleAgentExecutionEvent) can find pre-existing executions and
 * process real-time updates for them.
 */
function seedIndividualExecutionCache(client: QueryClient, executions: PlainAgentExecution[]) {
    for (const execution of executions) {
        const key = agentServiceQueryKey.getAgentExecution(execution.id);
        // Only seed if not already cached — avoid overwriting fresher data
        // from a direct getAgentExecution call or event update.
        if (!client.getQueryData(key)) {
            client.setQueryData(key, execution);
        }
    }
}

function setAgentExecutionInCache(client: QueryClient, agentExecutionId: string, agentExecution?: PlainAgentExecution) {
    // Update the individual execution cache
    client.setQueryData(
        agentServiceQueryKey.getAgentExecution(agentExecutionId),
        agentExecution ? agentExecution : null,
    );

    // Update per-environment caches
    const envIds = agentExecution ? agentExecutionEnvironmentIds(agentExecution) : [];

    // For updates/creates: update or add to each environment's cache
    for (const envId of envIds) {
        client.setQueryData(agentServiceQueryKey.byEnvironment(envId), (currentData?: PlainAgentExecution[]) => {
            if (!currentData) {
                // No cache for this environment yet — skip.
                // The next query will pick it up.
                return currentData;
            }

            const existingIndex = currentData.findIndex((e) => e.id === agentExecutionId);
            if (existingIndex >= 0) {
                // Update in place
                if (agentExecution) {
                    const updated = [...currentData];
                    updated[existingIndex] = agentExecution;
                    return updated;
                }
                // Delete
                return currentData.filter((e) => e.id !== agentExecutionId);
            }

            // Add new execution (prepend — newest first)
            if (agentExecution) {
                return [agentExecution, ...currentData];
            }
            return currentData;
        });
    }

    // For deletes: remove from all per-environment caches (we may not know
    // which environment owned it). Uses the prefix key convention (.slice)
    // to match all byEnvironment(*) queries in a single call.
    if (!agentExecution) {
        client.setQueriesData<PlainAgentExecution[]>(
            { queryKey: agentServiceQueryKey.byEnvironment().slice(0, -1) },
            (currentData) => currentData?.filter((e) => e.id !== agentExecutionId),
        );
        void evictFromCache("conversation:" + agentExecutionId);
    }
}

export const useListPromptsOptions = (filter?: Partial<ListPromptsRequest_Filter>) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { value: isOnaAIEnabled, loading: isOnaAILoading } = useFlagOnaAIEnabled();

    // Create a complete filter object with defaults to avoid unsafe casting
    const completeFilter: ListPromptsRequest_Filter = {
        isTemplate: false,
        isCommand: false,
        command: "",
        commandPrefix: "",
        excludePromptContent: false,
        ...filter,
    } as ListPromptsRequest_Filter;

    return queryOptions({
        queryKey: agentServiceQueryKey.listPrompts(completeFilter),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const response = await api.agentService.listPrompts({
                pagination: create(PaginationRequestSchema, { pageSize: DEFAULT_PAGE_SIZE }),
                filter: completeFilter,
            });
            return {
                prompts: response.prompts.map(toPlainPrompt),
            };
        },
        enabled: !!user && !isOnaAILoading && isOnaAIEnabled,
    });
};

export const useListPrompts = (filter?: Partial<ListPromptsRequest_Filter>) => {
    return useQuery(useListPromptsOptions(filter));
};

export const useListCommandPrompts = () => {
    return useListPrompts({
        isCommand: true,
        excludePromptContent: true,
    });
};

/**
 * Lists all skill prompts (prompts with isCommand=true).
 * Skills are organization-wide prompts that can be invoked via slash commands.
 */
export const useListSkillPrompts = () => {
    return useListPrompts({
        isCommand: true,
        excludePromptContent: true,
    });
};

/**
 * Infinite-scroll hook for command prompts with server-side search.
 * Accumulates pages as the user scrolls. Resets when the search term changes.
 */
export const useCommandPromptsInfinite = (search = "") => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { value: isOnaAIEnabled, loading: isOnaAILoading } = useFlagOnaAIEnabled();

    return useInfiniteQuery({
        queryKey: agentServiceQueryKey.listPromptsInfinite(search),
        queryFn: async ({ pageParam = "" }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const response = await api.agentService.listPrompts({
                pagination: create(PaginationRequestSchema, { pageSize: DEFAULT_PAGE_SIZE, token: pageParam }),
                filter: {
                    isCommand: true,
                    excludePromptContent: true,
                    search,
                } as ListPromptsRequest_Filter,
            });
            return {
                prompts: response.prompts.map(toPlainPrompt),
                nextToken: response.pagination?.nextToken ?? "",
            };
        },
        getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
        initialPageParam: "",
        enabled: !!user && !isOnaAILoading && isOnaAIEnabled,
        placeholderData: keepPreviousData,
    });
};

export const useGetPromptOptions = (promptId?: string) => {
    const api = useGitpodAPI();

    return queryOptions({
        queryKey: agentServiceQueryKey.getPrompt(promptId),
        queryFn: async () => {
            const response = await api.agentService.getPrompt({
                promptId,
            });
            if (!response.prompt) {
                throw new Error("Prompt not found");
            }
            return toPlainPrompt(response.prompt);
        },
        enabled: !!promptId,
    });
};

export const useGetPrompt = (promptId?: string) => {
    return useQuery(useGetPromptOptions(promptId));
};
